import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import Button from '../components/common/Button'
import Card from '../components/common/Card'
import ParrotMark from '../components/common/ParrotMark'
import Spinner from '../components/common/Spinner'
import { GemGlyph } from '../components/shop/GemCards'
import { faDigits } from '../lib/numerals'
import { AVATARS, RARITY_LABEL, RARITY_TILE, TONE, canAfford, freezeState, gemsOn, messageFor, owns, priceOf, refusalFor, visiblePacks } from '../lib/shop'
import { classifyFailure } from '../services/api'
import { buyAvatar, buyPack, convertStreak, selectAvatar, shopCatalog, shopWallet } from '../services/shop'

function CoinGlyph({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9.5" fill="currentColor" />
      <circle cx="12" cy="12" r="5" fill="currentColor" opacity="0.55" />
    </svg>
  )
}

const GLYPH = {
  flame: <path d="M12 3c1 3 4 5 4 9a4 4 0 0 1-8 0c0-1.5.5-2.5 1-3 .5 1.5 1.5 2 2 2 0-3-1-5 1-8z" />,
  shield: <path d="M12 3l7 2.6v5.6c0 4.3-2.9 7.6-7 8.8-4.1-1.2-7-4.5-7-8.8V5.6z" />,
  bolt: <path d="M13 2L5 13h5l-1 9 8-11h-5z" />,
}

function Glyph({ kind, size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {GLYPH[kind] ?? GLYPH.flame}
    </svg>
  )
}

/** A price: the coin, the gem or the flame, then the number — in days for streak-priced items. */
function Price({ price, streakPriced, gemPriced }) {
  const { t } = useTranslation('shop')
  return (
    <>
      {streakPriced ? <Glyph kind="flame" /> : gemPriced ? <GemGlyph /> : <CoinGlyph />}
      <span>{streakPriced ? t('page.priceDaySuffix', { count: faDigits(price) }) : faDigits(price)}</span>
    </>
  )
}

/**
 * The wallet. Gems off (production until stage 3): coins, streak and the
 * gift chest, exactly as before. Gems on (V6-b-gem): gems replace the coin
 * tile, the chest bar counts toward the day's gem, and the freezes read
 * `x/max`.
 */
export function WalletCard({ wallet }) {
  const { t } = useTranslation('shop')
  const gems = gemsOn(wallet)
  const freezes = freezeState(wallet)
  const chestPractices = Math.max(wallet.chestPractices ?? 5, 1)
  const chestLeft = Math.max(chestPractices - (wallet.chestDone ?? 0), 0)
  const fraction = Math.min(Math.max((wallet.chestDone ?? 0) / chestPractices, 0), 1)
  return (
    <section className="space-y-3.5 rounded-[22px] bg-linear-to-br from-hero-from to-hero-to p-4 text-white">
      <div className="grid grid-cols-2 gap-2.5">
        {gems ? (
          <div className="flex items-center gap-2.5 rounded-[14px] bg-white/15 p-3" data-wallet="gems">
            <span className="text-accent"><GemGlyph size={22} /></span>
            <span className="flex flex-col">
              <span className="text-[17px] font-bold leading-tight">{faDigits(wallet.gems ?? 0)}</span>
              <span className="text-[10.5px] text-white/75">{t('page.gemLabel')}</span>
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2.5 rounded-[14px] bg-white/15 p-3">
            <span className="text-accent"><CoinGlyph size={22} /></span>
            <span className="flex flex-col">
              <span className="text-[17px] font-bold leading-tight">{faDigits(wallet.coins ?? 0)}</span>
              <span className="text-[10.5px] text-white/75">{t('page.coinLabel')}</span>
            </span>
          </div>
        )}
        <div className="flex items-center gap-2.5 rounded-[14px] bg-white/15 p-3">
          <span className="text-accent"><Glyph kind="flame" size={22} /></span>
          <span className="flex flex-col">
            <span className="text-[17px] font-bold leading-tight">{faDigits(wallet.streakDays ?? 0)}</span>
            <span className="text-[10.5px] text-white/75">{t('page.streakDaysLabel')}</span>
          </span>
        </div>
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-[11.5px]">
          <span className="text-white/75">{gems ? t('page.untilNextGem') : t('page.untilNextChest')}</span>
          <span className="font-bold text-accent">{t('page.chestPracticeSuffix', { count: faDigits(chestLeft) })}</span>
        </div>
        <div className="h-[7px] overflow-hidden rounded-full bg-white/20" role="progressbar" aria-valuemin={0} aria-valuemax={chestPractices} aria-valuenow={wallet.chestDone ?? 0} aria-label={gems ? t('page.gemProgressAria') : t('page.chestProgressAria')}>
          <div className="h-full rounded-full bg-accent" style={{ width: `${fraction * 100}%` }} />
        </div>
      </div>
      {freezes && (
        <div className="flex items-center gap-2 rounded-[12px] bg-white/10 px-3 py-2 text-[11.5px]" data-freezes={`${freezes.held}/${freezes.max}`}>
          <Glyph kind="shield" size={16} />
          <span className="tnum flex-1 font-semibold">{t('page.freezeCount', { held: faDigits(freezes.held), max: faDigits(freezes.max) })}</span>
        </div>
      )}
    </section>
  )
}

function AvatarCard({ avatar, wallet, catalog, busy, onTap }) {
  const { t } = useTranslation('shop')
  const active = wallet.avatar === avatar.id
  const owned = owns(wallet, avatar.id)
  const priced = priceOf(catalog, avatar)
  const afford = canAfford(wallet, priced)
  const tone = TONE[avatar.tone] ?? TONE.brand
  const cta = active
    ? 'bg-brand-600 text-white dark:text-slate-900'
    : owned
      ? 'bg-brand-50 text-brand-700'
      : afford
        ? 'bg-surface-2 text-ink'
        : 'bg-surface-2 text-ink-3'
  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => onTap(avatar, owned)}
      aria-pressed={active}
      className={`flex flex-col items-center gap-2.5 rounded-[18px] bg-surface px-3 py-3.5 text-center transition disabled:cursor-wait ${
        active ? 'border-[1.5px] border-brand-600' : 'border border-line'
      }`}
    >
      <span className="relative">
        <span className={`grid size-[74px] place-items-center rounded-full border-[2.5px] ${tone.disc} ${active ? tone.ring : 'border-transparent'}`}>
          <ParrotMark size={42} />
        </span>
        <span className={`absolute -end-1 -top-1 rounded-full px-[7px] py-[3px] text-[9.5px] font-bold ${RARITY_TILE[avatar.rarity] ?? RARITY_TILE.base}`}>
          {RARITY_LABEL[avatar.rarity] ?? RARITY_LABEL.base}
        </span>
      </span>
      <span className="text-[13px] font-semibold text-ink">{avatar.name}</span>
      <span className={`flex h-8 w-full items-center justify-center gap-1.5 rounded-[9px] text-xs font-bold ${cta}`}>
        {active ? t('page.selected') : owned ? t('page.select') : <Price price={priced.price} streakPriced={priced.streakPriced} gemPriced={priced.gemPriced} />}
      </span>
    </button>
  )
}

export function PackRow({ pack, wallet, catalog, busy, onTap }) {
  const { t } = useTranslation('shop')
  const priced = priceOf(catalog, pack)
  // The freeze cap (V6-b-gem): a full wallet cannot take another freeze — the server would answer `freeze_limit`.
  const freezes = pack.id === 'freeze' ? freezeState(wallet) : null
  const full = freezes?.full === true
  const afford = canAfford(wallet, priced) && !full
  return (
    <Card
      as="button"
      type="button"
      disabled={busy || full}
      data-pack={pack.id}
      onClick={() => onTap(pack)}
      pad="p-3.5"
      className="flex w-full items-center gap-3 text-start transition disabled:cursor-wait"
    >
      <span className={`grid size-12 shrink-0 place-items-center rounded-[14px] ${priced.streakPriced ? 'bg-accent-tint text-accent-ink' : 'bg-brand-50 text-brand-700'}`}>
        <Glyph kind={pack.icon} size={22} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex items-center gap-2">
          <span className="text-sm font-bold text-ink">{pack.name}</span>
          <span className={`rounded-full px-[7px] py-0.5 text-[9.5px] font-bold ${pack.popular ? 'bg-accent text-on-accent' : 'bg-surface-2 text-ink-2'}`}>{pack.badge}</span>
        </span>
        <span className="text-[11.5px] leading-[1.85] text-ink-2">{full ? t('page.freezeFullHint') : pack.description}</span>
        {freezes && (
          <span className="tnum text-[11px] font-semibold text-brand-700">{t('page.freezeCount', { held: faDigits(freezes.held), max: faDigits(freezes.max) })}</span>
        )}
      </span>
      <span className={`flex h-[34px] shrink-0 items-center gap-1.5 rounded-[10px] px-3 text-xs font-bold ${afford ? 'bg-brand-600 text-white dark:text-slate-900' : 'bg-surface-2 text-ink-3'}`}>
        <Price price={priced.price} streakPriced={priced.streakPriced} gemPriced={priced.gemPriced} />
      </span>
    </Card>
  )
}

/**
 * The shop (design page 12, `shop`; P-shop of the go-live plan): the
 * learner's wallet, nine avatars, four streak packs, and the convert row —
 * all the server's (B5). A tap is a request the server either does, in one
 * transaction, or refuses by name; the page draws and reports, and decides
 * nothing. While one request is on its way the others are disabled rather
 * than queued, so a double tap converts one day, not two.
 */
export default function Shop() {
  const { t } = useTranslation('shop')
  const [wallet, setWallet] = useState(null)
  const [catalog, setCatalog] = useState(null)
  const [error, setError] = useState(null)
  const [reload, setReload] = useState(0)
  const [tab, setTab] = useState('avatars')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState(null)

  useEffect(() => {
    let cancelled = false
    setError(null)
    shopWallet()
      .then((data) => {
        if (!cancelled) setWallet(data)
      })
      .catch((e) => {
        if (!cancelled) setError(e)
      })
    shopCatalog()
      .then((data) => {
        if (!cancelled) setCatalog(data)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [reload])

  async function request(kind, name, call) {
    if (busy) return
    setBusy(true)
    try {
      const result = await call()
      setWallet(result.wallet)
      setMessage(messageFor(result.outcome, name))
    } catch (e) {
      setMessage(refusalFor(e, kind))
    } finally {
      setBusy(false)
    }
  }

  function tapAvatar(avatar, owned) {
    request('avatar', avatar.name, () => (owned ? selectAvatar(avatar.id) : buyAvatar(avatar.id)))
  }

  function tapPack(pack) {
    request('pack', pack.name, () => buyPack(pack.id))
  }

  function tapConvert() {
    request('convert', '', () => convertStreak())
  }

  function switchTab(next) {
    setTab(next)
    setMessage(null)
  }

  return (
    <div className="mx-auto max-w-md space-y-3.5" aria-busy={busy || undefined}>
      <header className="flex items-center gap-2">
        <Link to="/profile" aria-label={t('page.backToProfile')} className="grid size-11 place-items-center rounded-xl text-ink hover:bg-surface-2">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 6l6 6-6 6" />
          </svg>
        </Link>
        <h1 className="flex-1 text-lg font-bold text-ink">{t('page.title')}</h1>
      </header>

      {error && !wallet ? (
        <div className="space-y-3">
          <div role="status" className={`rounded-xl p-3 text-sm ${classifyFailure(error) === 'offline' ? 'bg-warn-tint text-warn-ink' : 'bg-bad-tint text-bad-ink'}`}>
            {classifyFailure(error) === 'offline' ? t('page.walletOfflineError') : t('page.walletServerError')}
          </div>
          <Button variant="secondary" className="w-full" onClick={() => setReload((n) => n + 1)}>
            {t('page.retry')}
          </Button>
        </div>
      ) : !wallet ? (
        <Spinner label={t('page.loadingWallet')} />
      ) : (
        <div className={`space-y-3.5 transition-opacity ${busy ? 'opacity-70' : ''}`}>
          <WalletCard wallet={wallet} />

          <div className="grid grid-cols-2 gap-1 rounded-xl bg-surface-2 p-1">
            {[
              ['avatars', t('page.tabAvatars')],
              ['streak', gemsOn(wallet) ? t('page.tabStreakGems') : t('page.tabStreak')],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                aria-pressed={tab === key}
                onClick={() => switchTab(key)}
                className={`h-9 rounded-lg text-[12.5px] font-semibold transition-colors ${tab === key ? 'bg-surface text-brand-700 shadow-sm' : 'text-ink-2'}`}
              >
                {label}
              </button>
            ))}
          </div>

          {tab === 'avatars' ? (
            <div className="grid grid-cols-2 gap-2.5">
              {AVATARS.map((avatar) => (
                <AvatarCard key={avatar.id} avatar={avatar} wallet={wallet} catalog={catalog} busy={busy} onTap={tapAvatar} />
              ))}
            </div>
          ) : (
            <div className="space-y-2.5">
              {visiblePacks(catalog, gemsOn(wallet)).map((pack) => (
                <PackRow key={pack.id} pack={pack} wallet={wallet} catalog={catalog} busy={busy} onTap={tapPack} />
              ))}
              {/* The streak-to-coins trade is closed while gems are on (`coins_retired`). */}
              {!gemsOn(wallet) && (
                <div className="flex items-center gap-3 rounded-2xl bg-accent-tint p-3.5 text-accent-ink">
                  <span className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="text-[13px] font-bold">{t('page.convertRow')}</span>
                    <span className="text-[11px]">{t('page.convertRate', { coins: faDigits(catalog?.streakToCoins ?? 200) })}</span>
                  </span>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={tapConvert}
                    className="h-[34px] shrink-0 rounded-[10px] bg-accent px-3.5 text-xs font-bold text-on-accent disabled:cursor-wait"
                  >
                    {t('page.convertButton')}
                  </button>
                </div>
              )}
            </div>
          )}

          {message && (
            <p role="status" className={`rounded-[14px] px-3.5 py-3 text-xs font-semibold leading-[1.85] ${message.good ? 'bg-good-tint text-good-ink' : 'bg-bad-tint text-bad-ink'}`}>
              {message.text}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
